import { useCallback, useMemo } from 'react';
import { OrderedData } from '../components/CampaignOrder';
import { useCitation } from '../Providers/CitationProvider';
import {
  DEFAULT_PRICE_FOR_REMOVE_DUPLICATES,
  DEFAULT_PRICE_FOR_AVAILABLE_NEW_CITATIONS
} from '../constants';

interface UseCampaignOrderDataOptions {
  /**
   * Whether to include onRemove callbacks in the order data
   * @default true
   */
  enableRemoveActions?: boolean;
}

/**
 * Custom hook to generate campaign order table data
 * - Calculates prices for selected citations
 * - Builds order rows for display
 * - Handles remove actions (optional)
 */
export const useCampaignOrderData = (
  options: UseCampaignOrderDataOptions = {}
): OrderedData[] => {
  const { enableRemoveActions = true } = options;

  const {
    state,
    setSyncAndPost,
    setSelectedExistingCitations,
    setSelectedAvailableNewCitations
  } = useCitation();

  // Calculate total price for selected existing citations
  const getTotalPriceForSelectedExistingCitations = useCallback(() => {
    const citations = state.campaignDetails?.existingCitations?.citations;
    if (!citations || citations.length === 0) {
      return 0;
    }
    return citations
      .filter((citation) =>
        state.selectedExistingCitations.includes(citation.domain)
      )
      .reduce((total, citation) => {
        const price = citation.cost ?? 0;
        return total + price;
      }, 0);
  }, [
    state.campaignDetails?.existingCitations?.citations,
    state.selectedExistingCitations
  ]);

  // Build table data from global state
  const tableData = useMemo<OrderedData[]>(() => {
    const tableRows: OrderedData[] = [];

    // Manual Submissions (Update)
    if (state.selectedExistingCitations.length > 0) {
      tableRows.push({
        id: 1,
        title: `${state.selectedExistingCitations.length}x Manual Submissions (Update)`,
        price: getTotalPriceForSelectedExistingCitations(),
        ...(enableRemoveActions && {
          onRemove: () => setSelectedExistingCitations([])
        })
      });
    }

    // Manual Submissions (Create)
    if (state.selectedAvailableNewCitations.length > 0) {
      // Calculate total price from actual citation costs
      const citations = state.campaignDetails?.availableCitations?.citations;
      const totalPrice = citations
        ? citations
            .filter((citation) =>
              state.selectedAvailableNewCitations.some((item) => item.citationSite === citation.citationSite)
            )
            .reduce((total, citation) => total + (citation.cost ?? 0), 0)
        : state.selectedAvailableNewCitations.length *
          DEFAULT_PRICE_FOR_AVAILABLE_NEW_CITATIONS;

      tableRows.push({
        id: 2,
        title: `${state.selectedAvailableNewCitations.length}x Manual Submissions (Create)`,
        price: totalPrice,
        ...(enableRemoveActions && {
          onRemove: () => setSelectedAvailableNewCitations([])
        })
      });
    }

    // Remove Harmful Duplicate Listings
    if (state.campaignDetails?.existingCitations?.citations.length > 0) {
      tableRows.push({
        id: 3,
        title: `${state.campaignDetails.existingCitations.citations.length}x Remove Harmful Duplicate Listings`,
        price:
          state.campaignDetails.existingCitations.citations.length *
          DEFAULT_PRICE_FOR_REMOVE_DUPLICATES
      });
    }

    // Sync and Post for 12 months
    if (state.syncAndPost) {
      tableRows.push({
        id: 4,
        title: 'Sync and Post for 12 months',
        price: 50,
        ...(enableRemoveActions && {
          onRemove: () => setSyncAndPost(false)
        })
      });
    }

    return tableRows;
  }, [
    state.selectedExistingCitations,
    state.selectedAvailableNewCitations,
    state.campaignDetails?.existingCitations?.citations,
    state.campaignDetails?.availableCitations?.citations,
    state.syncAndPost,
    getTotalPriceForSelectedExistingCitations,
    enableRemoveActions,
    setSelectedExistingCitations,
    setSelectedAvailableNewCitations,
    setSyncAndPost
  ]);

  return tableData;
};

